import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function RecordModal({ open, onClose, onTranscript, endpoint = "/transcribe" }) {
  const [status, setStatus] = useState("idle"); // idle | recording | processing
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) start();
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    mediaRef.current = null;
    chunksRef.current = [];
  };

  const start = async () => {
    setSeconds(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mediaRef.current = mr;
      mr.start();
      setStatus("recording");
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("We couldn't access your microphone. Please allow access and try again.");
      onClose();
    }
  };

  const stop = () => {
    if (mediaRef.current && status === "recording") {
      if (timerRef.current) clearInterval(timerRef.current);
      mediaRef.current.stop();
      setStatus("processing");
    }
  };

  const handleStop = async () => {
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (blob.size < 1000) {
      toast.error("That recording was too short. Please try again.");
      onClose();
      return;
    }
    try {
      const form = new FormData();
      form.append("file", blob, "answer.webm");
      const { data } = await api.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onTranscript(data.transcript || "", data.audio_id || null, data.raw_transcript || null);
      onClose();
    } catch (e) {
      toast.error("We couldn't transcribe that. You can try again or type instead.");
      onClose();
    }
  };

  if (!open) return null;
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-charcoal/40 backdrop-blur-sm animate-fade-in" data-testid="record-modal">
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-8 shadow-2xl animate-fade-up">
        <div className="flex justify-between items-center mb-2">
          <p className="text-xs uppercase tracking-widest text-sage font-semibold">
            {status === "processing" ? "Writing it down" : "Listening"}
          </p>
          <button data-testid="record-close-btn" onClick={onClose} className="text-subtle hover:text-charcoal">
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col items-center py-6">
          {status === "processing" ? (
            <>
              <Loader2 className="text-teal animate-spin" size={48} />
              <p className="mt-5 text-subtle">Turning your words into text…</p>
            </>
          ) : (
            <>
              <div className="flex items-end gap-1.5 h-10 mb-6">
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                  <span key={i} className="eq-bar" style={{ animationDelay: `${i * 0.12}s` }} />
                ))}
              </div>
              <div className="font-serif text-4xl text-charcoal tabular-nums" data-testid="record-timer">
                {mm}:{ss}
              </div>
              <p className="mt-2 text-subtle text-sm">Take your time. Just talk naturally.</p>
              <button
                data-testid="record-stop-btn"
                onClick={stop}
                className="mt-8 flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors animate-mic-pulse"
              >
                <Square size={20} fill="white" /> Finish
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
